// ==========================================================================
// screens/MfaVerify.tsx — every login: enter the authenticator code (FF-003)
// 10 Oct (FF-061): if this device has Face ID / fingerprint set up (My profile
// → Quick sign-in), one tap does the second step instead of the code.
// ==========================================================================
import { useEffect, useState, type FormEvent } from "react";
import { Fingerprint, Loader2 } from "lucide-react";
import { canUsePasskeys, listFactors, signInWithPasskey, type Factor } from "@/lib/quickSignIn";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { Brand } from "@/components/Brand";
import { Button, Card, ErrorText, Field } from "@/components/ui";

export default function MfaVerify() {
  const { refresh, signOut } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeys, setPasskeys] = useState<Factor[]>([]);
  const [pkBusy, setPkBusy] = useState(false);
  useEffect(() => { void listFactors().then((fs) => setPasskeys(fs.filter((f) => f.type === "webauthn" && f.verified))); }, []);
  const usePasskey = async () => {
    setPkBusy(true); setError(null);
    const r = await signInWithPasskey(passkeys[0].id);
    setPkBusy(false);
    if (!r.ok) { setError(r.message); return; }
    await refresh();
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const factor = (factors?.totp ?? []).find((f) => f.status === "verified");
    if (!factor) { setBusy(false); setError("No authenticator found. Sign out and sign in again."); return; }
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
    if (chErr || !ch) { setBusy(false); setError("Couldn't check the code. Try again."); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId: factor.id, challengeId: ch.id, code: code.trim() });
    setBusy(false);
    if (vErr) { setError("That code didn't match. Wait for a fresh one and try again."); return; }
    await refresh();
  }

  return (
    <main className="mx-auto max-w-md px-4 py-10 space-y-6">
      <Brand />
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {passkeys.length > 0 && canUsePasskeys() && (
            <div className="space-y-3 border-b border-line pb-4">
              <button type="button" onClick={usePasskey} disabled={pkBusy} className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-base font-semibold text-primary-ink disabled:opacity-60">
                {pkBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Fingerprint className="h-5 w-5" />}Use Face ID or fingerprint
              </button>
              <p className="text-center text-xs text-ink-muted">or type the code from your authenticator app</p>
            </div>
          )}
          <h2 className="text-lg font-semibold">Enter your authenticator code</h2>
          <Field label="6-digit code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required autoFocus value={code} onChange={(e) => setCode(e.target.value)} />
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={busy}>{busy ? "Checking…" : "Continue"}</Button>
          <Button type="button" variant="ghost" onClick={signOut}>Sign out</Button>
        </form>
      </Card>
    </main>
  );
}
